import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { useMemo } from "react"
import type { ILanguageSignal } from "../../hooks/useStreamingTranscript"
import { languageDisplayName } from "../../lib/language-display"
import classes from "./LanguagePill.module.css"

interface IProps {
  languageCode?: string
  languageSignal?: ILanguageSignal | null
  languageIsWorkspaceDefault?: boolean
}

/**
 * Current meeting language, plus a quiet hint while another language is the
 * live candidate. No countdown — the audio-second counter was not the clock
 * people were watching. Pure + dumb. Props are unchanged.
 */
export function LanguagePill({
  languageCode,
  languageSignal = null,
  languageIsWorkspaceDefault = false,
}: IProps): React.JSX.Element | null {
  const { i18n } = useLingui()

  const currentName = useMemo(
    () => (languageCode ? languageDisplayName(languageCode, i18n.locale) : ""),
    [languageCode, i18n.locale],
  )

  const candidate = languageSignal?.candidate ?? null
  const candidateName = useMemo(
    () => (candidate ? languageDisplayName(candidate, i18n.locale) : ""),
    [candidate, i18n.locale],
  )

  if (!currentName && !candidateName) return null

  return (
    <div className={classes.root} data-testid="language-pill">
      {currentName ? (
        <span className={classes.current}>
          {currentName}
          {languageIsWorkspaceDefault ? (
            <>
              {" · "}
              <Trans>workspace default</Trans>
            </>
          ) : null}
        </span>
      ) : null}
      {candidateName ? (
        <span className={classes.hint} data-testid="language-pill-candidate">
          <Trans>Sounds like {candidateName}</Trans>
        </span>
      ) : null}
    </div>
  )
}
